import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  assignRole,
  removeRole,
  getCoachesByClub,
  type User,
  type UserRole,
} from "../../services/users";
import { getClubs } from "../../services/api";
import { type Club } from "../../services/clubs";
import { useAuth } from "../../context/AuthContext";
import DateInput from "../../components/DateInput";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { formatDate } from "../../lib/dateFormat";

type Coach = {
  id: string;
  full_name: string | null;
  email: string;
  phone: string | null;
};

export default function UserManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeRole } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [clubFilter, setClubFilter] = useState("");

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Bulk selection states
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    date_of_birth: "",
    gender: "",
    phone: "",
    city: "",
    county: "",
    country: "Romania",
    locale: "ro",
    role_code: "athlete",
    club_id: "",
    team_id: "",
    has_guardian: false,
    guardian_full_name: "",
    guardian_email: "",
    guardian_phone: "",
    guardian_relationship: "mother",
  });

  const [roleFormData, setRoleFormData] = useState({
    code: "athlete",
    scope_type: "club" as "platform" | "club" | "team",
    scope_id: "",
  });

  useEffect(() => {
    fetchUsers();
    fetchClubs();
  }, [page, searchQuery, roleFilter, clubFilter]);

  // Fetch coaches when club is selected in create form
  useEffect(() => {
    if (formData.club_id && showCreateModal) {
      fetchCoaches(formData.club_id);
    } else {
      setCoaches([]);
    }
  }, [formData.club_id, showCreateModal]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getUsers({
        q: searchQuery,
        role: roleFilter,
        clubId: clubFilter,
        page,
        pageSize: 20,
      });
      setUsers(data.data);
      setTotal(data.total);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const data = await getClubs();
      setClubs(data.map((d) => d.club));
    } catch (error) {
      console.error("Failed to fetch clubs:", error);
    }
  };

  const fetchCoaches = async (clubId: string) => {
    try {
      const data = await getCoachesByClub(clubId);
      setCoaches(data);
    } catch (error) {
      console.error("Failed to fetch coaches:", error);
      setCoaches([]);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Build payload (password not needed - user will set it via email link)
      const payload: any = {
        email: formData.email,
        full_name: formData.full_name || undefined,
        date_of_birth: formData.date_of_birth || undefined,
        gender: formData.gender || undefined,
        phone: formData.phone || undefined,
        city: formData.city || undefined,
        county: formData.county || undefined,
        country: formData.country || "Romania",
        locale: formData.locale,
      };

      // Add guardian contact if applicable
      if (formData.has_guardian && formData.role_code === "athlete") {
        payload.guardian_contact = {
          full_name: formData.guardian_full_name,
          email: formData.guardian_email,
          phone: formData.guardian_phone,
          relationship: formData.guardian_relationship,
        };
      }

      // Add team_id if selected
      if (formData.team_id) {
        payload.team_id = formData.team_id;
      }

      // Build the roles payload. Platform admins get a platform-scoped
      // role with no club attachment — every other role needs a club.
      if (formData.role_code === "platform_admin") {
        payload.roles = [
          {
            code: "platform_admin",
            scope_type: "platform",
            scope_id: null,
          },
        ];
      } else if (formData.role_code && formData.club_id) {
        payload.roles = [
          {
            code: formData.role_code,
            scope_type: "club",
            scope_id: formData.club_id,
          },
        ];
      }

      await createUser(payload);
      toast.success("Utilizator creat cu succes! Se va trimite un email pentru resetarea parolei.");
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (error: any) {
      console.error("Failed to create user:", error);
      toast.error(error.response?.data?.error || "Crearea utilizatorului a eșuat");
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await updateUser(selectedUser.id, {
        full_name: formData.full_name,
        date_of_birth: formData.date_of_birth || undefined,
        gender: formData.gender || undefined,
        phone: formData.phone || undefined,
        locale: formData.locale,
      });
      toast.success("Utilizator actualizat cu succes!");
      setShowEditModal(false);
      setSelectedUser(null);
      resetForm();
      fetchUsers();
    } catch (error: any) {
      console.error("Failed to update user:", error);
      toast.error(error.response?.data?.error || "Actualizarea utilizatorului a eșuat");
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await deleteUser(selectedUser.id);
      toast.success("Utilizator șters cu succes!");
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error: any) {
      console.error("Failed to delete user:", error);
      toast.error(error.response?.data?.error || "Ștergerea utilizatorului a eșuat");
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === users.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(users.map(u => u.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setBulkDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of Array.from(selectedIds)) {
      try {
        await deleteUser(id);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    setBulkDeleting(false);
    setShowBulkDeleteModal(false);
    setSelectedIds(new Set());

    if (successCount > 0) {
      toast.success(`${successCount} utilizator(i) șters(i) cu succes!`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} utilizator(i) nu au putut fi șterși`);
    }

    fetchUsers();
  };

  const handleAssignRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await assignRole(selectedUser.id, roleFormData);
      toast.success("Rol atribuit cu succes!");
      setShowRoleModal(false);
      setSelectedUser(null);
      resetRoleForm();
      fetchUsers();
    } catch (error: any) {
      console.error("Failed to assign role:", error);
      toast.error(error.response?.data?.error || "Atribuirea rolului a eșuat");
    }
  };

  const handleRemoveRole = async (roleId: string) => {
    if (!selectedUser) return;
    if (!window.confirm("Sigur vrei să elimini acest rol?")) return;
    try {
      await removeRole(selectedUser.id, roleId);
      toast.success("Rol eliminat cu succes!");
      fetchUsers();
    } catch (error: any) {
      console.error("Failed to remove role:", error);
      toast.error(error.response?.data?.error || "Eliminarea rolului a eșuat");
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      full_name: "",
      date_of_birth: "",
      gender: "",
      phone: "",
      city: "",
      county: "",
      country: "Romania",
      locale: "ro",
      role_code: "athlete",
      club_id: "",
      team_id: "",
      has_guardian: false,
      guardian_full_name: "",
      guardian_email: "",
      guardian_phone: "",
      guardian_relationship: "mother",
    });
    setCoaches([]);
  };

  const resetRoleForm = () => {
    setRoleFormData({
      code: "athlete",
      scope_type: "club",
      scope_id: "",
    });
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name || "",
      date_of_birth: user.date_of_birth ? user.date_of_birth.split("T")[0] : "",
      gender: user.gender || "",
      phone: user.phone || "",
      city: "",
      county: "",
      country: "Romania",
      locale: user.locale,
      role_code: "athlete",
      club_id: "",
      team_id: "",
      has_guardian: false,
      guardian_full_name: "",
      guardian_email: "",
      guardian_phone: "",
      guardian_relationship: "mother",
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const openRoleModal = (user: User) => {
    setSelectedUser(user);
    resetRoleForm();
    setShowRoleModal(true);
  };

  const totalPages = Math.ceil(total / 20);

  // /admin/users is platform-admin only. A club_admin landing here (e.g.
  // via an old "+ Adauga sportiv" link) sees a page with no sidebar, so
  // the hamburger button has nothing to open. Redirect them to the
  // dashboard root, which will pick the right per-role landing page.
  if (activeRole && activeRole !== "platform_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Utilizatori și membri"
          title={t("admin.userManagement.title", "Gestionare utilizatori")}
          subtitle={t("admin.userManagement.description", "Gestionează utilizatori, roluri și permisiuni")}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
            </svg>
          }
        />

        {/* User Type Navigation Buttons */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6">
          <h2 className="text-sm font-semibold text-slate-600 mb-4 uppercase tracking-wide">Vezi utilizatori pe tipuri</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate("/admin/athletes")}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Vezi Sportivi
            </button>
            <button
              onClick={() => navigate("/admin/coaches")}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Vezi Antrenori
            </button>
            <button
              onClick={() => navigate("/admin/club-admins")}
              className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Vezi Club Admins
            </button>
            <button
              onClick={() => navigate("/admin/platform-admins")}
              className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Vezi Platform Admins
            </button>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t("admin.userManagement.searchPlaceholder", "Caută după nume sau email...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">{t("admin.userManagement.allRoles", "Toate rolurile")}</option>
              <option value="platform_admin">Admin platformă</option>
              <option value="club_admin">Admin club</option>
              <option value="coach">Antrenor</option>
              <option value="athlete">Sportiv</option>
              <option value="parent">Părinte</option>
            </select>

            <select
              value={clubFilter}
              onChange={(e) => setClubFilter(e.target.value)}
              className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">{t("admin.userManagement.allClubs", "Toate cluburile")}</option>
              {clubs.map((club) => (
                <option key={club.id} value={club.id}>
                  {club.name}
                </option>
              ))}
            </select>

            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t("admin.userManagement.createUser", "Creează utilizator")}
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
            <span className="text-blue-700 font-medium">
              {selectedIds.size} utilizator(i) selectat(i)
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
              >
                Deselectează
              </button>
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Șterge Selectate
              </button>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-xl font-bold text-slate-800 mb-2">Confirmare Ștergere</h3>
              <p className="text-slate-600 mb-6">
                Ești sigur că vrei să ștergi {selectedIds.size} utilizator(i)? Această acțiune nu poate fi anulată.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={bulkDeleting}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors disabled:opacity-50"
                >
                  Anulează
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {bulkDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Se șterge...
                    </>
                  ) : (
                    "Confirmă Ștergerea"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="flex items-center justify-center gap-3">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-slate-600 font-medium">{t("common.loading", "Se încarcă...")}</span>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center">
              <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-slate-500">{t("admin.userManagement.noUsers", "Niciun utilizator găsit")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-4 text-left">
                      <input
                        type="checkbox"
                        checked={users.length > 0 && selectedIds.size === users.length}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      {t("admin.userManagement.email", "Email")}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      {t("admin.userManagement.name", "Nume")}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      {t("admin.userManagement.roles", "Roluri")}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      {t("admin.userManagement.status", "Status")}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      {t("admin.userManagement.createdAt", "Creat")}
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      {t("common.actions", "Acțiuni")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((user) => (
                    <tr key={user.id} className={`hover:bg-slate-50 transition-colors duration-150 ${selectedIds.has(user.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(user.id)}
                          onChange={() => toggleSelect(user.id)}
                          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {user.full_name || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex flex-wrap gap-1">
                          {user.roles?.map((role) => (
                            <span
                              key={role.id}
                              className="px-2 py-1 bg-slate-100 border border-slate-200 rounded-lg text-xs text-slate-600 font-medium"
                            >
                              {role.code}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(user as any).status && (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            (user as any).status === "active" ? "bg-green-100 text-green-700" :
                            (user as any).status === "demo" ? "bg-blue-100 text-blue-700" :
                            "bg-red-100 text-red-700"
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              (user as any).status === "active" ? "bg-green-500" :
                              (user as any).status === "demo" ? "bg-blue-500" :
                              "bg-red-500"
                            }`}></span>
                            {(user as any).status === "active" ? "Activ" :
                             (user as any).status === "demo" ? "Demo" : "Inactiv"}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {formatDate(new Date(user.created_at))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          className="text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          {t("common.viewDetails", "Vezi")}
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-emerald-600 hover:text-emerald-700 transition-colors"
                        >
                          {t("common.edit", "Editează")}
                        </button>
                        <button
                          onClick={() => openRoleModal(user)}
                          className="text-slate-600 hover:text-slate-700 transition-colors"
                        >
                          {t("admin.userManagement.manageRoles", "Roluri")}
                        </button>
                        <button
                          onClick={() => openDeleteModal(user)}
                          className="text-red-600 hover:text-red-700 transition-colors"
                        >
                          {t("common.delete", "Șterge")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="text-sm text-slate-500">
                {t("common.showing", "Se afișează")} {(page - 1) * 20 + 1} - {Math.min(page * 20, total)}{" "}
                {t("common.of", "din")} {total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-all duration-200"
                >
                  {t("common.previous", "Anterior")}
                </button>
                <span className="px-4 py-2 text-slate-600">
                  {t("common.page", "Pagina")} {page} {t("common.of", "din")} {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 transition-all duration-200"
                >
                  {t("common.next", "Următor")}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Create User Modal - Enhanced Multi-Section */}
        {showCreateModal && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto p-4 animate-fade-in-fast"
            onClick={() => { setShowCreateModal(false); resetForm(); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-slate-200 animate-modal-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Sticky header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-800">
                  {t("admin.userManagement.createUser", "Creează utilizator")}
                </h2>
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="w-8 h-8 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
                  aria-label={t("common.close", "Închide")}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <form onSubmit={handleCreateUser} className="flex flex-col min-h-0 flex-1">
                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

                  {/* Section 1: Personal Details */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Detalii personale</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-3">
                      <div className="col-span-2 md:col-span-2">
                        <label className="block text-xs font-medium text-slate-600 mb-1">Nume complet *</label>
                        <input
                          type="text"
                          required
                          value={formData.full_name}
                          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                      </div>
                      <div className="col-span-2 md:col-span-1">
                        <label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Telefon</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Data nașterii</label>
                        <DateInput
                          value={formData.date_of_birth}
                          onChange={(iso) => setFormData({ ...formData, date_of_birth: iso })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Gen</label>
                        <select
                          value={formData.gender}
                          onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        >
                          <option value="">Selectează...</option>
                          <option value="male">Masculin</option>
                          <option value="female">Feminin</option>
                          <option value="other">Altul</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Oraș</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Județ</label>
                        <input
                          type="text"
                          value={formData.county}
                          onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Țară</label>
                        <input
                          type="text"
                          value={formData.country}
                          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        />
                      </div>
                    </div>
                    <p className="text-[11px] text-blue-600 mt-2 flex items-center gap-1">
                      <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Utilizatorul va primi un email pentru a-și seta parola
                    </p>
                  </div>

                  {/* Section 2: Role & Club Selection */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Rol și organizație</h3>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Rol *</label>
                        <select
                          required
                          value={formData.role_code}
                          onChange={(e) => setFormData({ ...formData, role_code: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                        >
                          <option value="athlete">Sportiv</option>
                          <option value="coach">Antrenor</option>
                          <option value="club_admin">Admin club</option>
                          <option value="platform_admin">Admin platformă</option>
                        </select>
                      </div>
                      {/* Club picker hides entirely for platform_admin —
                          platform admins don't belong to a club. */}
                      {formData.role_code !== "platform_admin" && (
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Club *</label>
                          <select
                            required={formData.role_code !== "platform_admin"}
                            value={formData.club_id}
                            onChange={(e) => setFormData({ ...formData, club_id: e.target.value, team_id: "" })}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                          >
                            <option value="">Selectează club...</option>
                            {clubs.map((club) => (
                              <option key={club.id} value={club.id}>
                                {club.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {formData.role_code === "platform_admin" && (
                        <div className="flex items-center">
                          <p className="text-[11px] text-slate-500 italic">
                            Admin platformă nu este asociat unui club anume — are acces global.
                          </p>
                        </div>
                      )}
                      {formData.club_id && formData.role_code === "athlete" && (
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-slate-600 mb-1">Antrenor (opțional)</label>
                          <select
                            value={formData.team_id}
                            onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                          >
                            <option value="">Selectează antrenor...</option>
                            {coaches.map((coach) => (
                              <option key={coach.id} value={coach.id}>
                                {coach.full_name || coach.email}
                              </option>
                            ))}
                          </select>
                          {coaches.length === 0 && formData.club_id && (
                            <p className="text-[11px] text-slate-500 mt-1">Niciun antrenor disponibil în acest club</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 3: Parent/Guardian Details (for Athletes only) */}
                  {formData.role_code === "athlete" && (
                    <div>
                      <label htmlFor="has_guardian" className="flex items-center gap-2 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          id="has_guardian"
                          checked={formData.has_guardian}
                          onChange={(e) => setFormData({ ...formData, has_guardian: e.target.checked })}
                          className="w-4 h-4 text-blue-600 bg-white border-slate-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Părinte/tutore</span>
                      </label>
                      {formData.has_guardian && (
                        <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Nume complet părinte/tutore</label>
                            <input
                              type="text"
                              value={formData.guardian_full_name}
                              onChange={(e) => setFormData({ ...formData, guardian_full_name: e.target.value })}
                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Email tutore</label>
                            <input
                              type="email"
                              value={formData.guardian_email}
                              onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })}
                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Telefon tutore</label>
                            <input
                              type="tel"
                              value={formData.guardian_phone}
                              onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })}
                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-slate-600 mb-1">Relație</label>
                            <select
                              value={formData.guardian_relationship}
                              onChange={(e) => setFormData({ ...formData, guardian_relationship: e.target.value })}
                              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            >
                              <option value="mother">Mamă</option>
                              <option value="father">Tată</option>
                              <option value="guardian">Tutore legal</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Sticky footer */}
                <div className="flex gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50/60">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition font-medium"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition font-medium shadow-lg shadow-blue-500/25"
                  >
                    {t("common.create", "Creează")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast"
            onClick={() => { setShowEditModal(false); setSelectedUser(null); resetForm(); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col border border-slate-200 animate-modal-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Sticky header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-800">
                  {t("admin.userManagement.editUser", "Editează utilizator")}
                </h2>
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setSelectedUser(null); resetForm(); }}
                  className="w-8 h-8 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
                  aria-label={t("common.close", "Închide")}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <form onSubmit={handleUpdateUser} className="flex flex-col min-h-0 flex-1">
                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t("admin.userManagement.email", "Email")}</label>
                      <input
                        type="email"
                        disabled
                        value={formData.email}
                        className="w-full px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-500 cursor-not-allowed"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t("admin.userManagement.fullName", "Nume complet")}</label>
                      <input
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t("admin.userManagement.phone", "Telefon")}</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t("admin.userManagement.dateOfBirth", "Data nașterii")}</label>
                      <DateInput
                        value={formData.date_of_birth}
                        onChange={(iso) => setFormData({ ...formData, date_of_birth: iso })}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                      />
                    </div>
                  </div>
                </div>

                {/* Sticky footer */}
                <div className="flex gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50/60">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedUser(null);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition font-medium"
                  >
                    {t("common.cancel", "Anulează")}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition font-medium shadow-lg shadow-blue-500/25"
                  >
                    {t("common.save", "Salvează")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedUser && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast"
            onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-red-200 animate-modal-in"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-slate-800">
                  {t("admin.userManagement.deleteUser", "Șterge utilizator")}
                </h2>
              </div>
              <p className="text-slate-600 mb-6">
                {t(
                  "admin.userManagement.deleteConfirm",
                  "Sigur vrei să ștergi"
                )}{" "}
                <strong className="text-slate-800">{selectedUser.email}</strong>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-medium"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-200 font-medium shadow-lg shadow-red-500/25"
                >
                  {t("common.delete", "Șterge")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manage Roles Modal */}
        {showRoleModal && selectedUser && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-fast"
            onClick={() => { setShowRoleModal(false); setSelectedUser(null); resetRoleForm(); }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-slate-200 animate-modal-in"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-slate-800 mb-6">
                {t("admin.userManagement.manageRoles", "Gestionează roluri")}
              </h2>
              <p className="text-slate-600 text-sm mb-4">{selectedUser.email}</p>

              {/* Existing Roles */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                  {t("admin.userManagement.currentRoles", "Roluri curente")}
                </h3>
                <div className="space-y-2">
                  {selectedUser.roles && selectedUser.roles.length > 0 ? (
                    selectedUser.roles.map((role) => (
                      <div
                        key={role.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200"
                      >
                        <span className="text-slate-700 text-sm">
                          {role.code} ({role.scope_type}
                          {role.scope_id ? `: ${role.scope_id.slice(0, 8)}...` : ""})
                        </span>
                        <button
                          onClick={() => handleRemoveRole(role.id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors"
                        >
                          {t("common.remove", "Elimină")}
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500 text-sm">
                      {t("admin.userManagement.noRoles", "Niciun rol atribuit")}
                    </p>
                  )}
                </div>
              </div>

              {/* Add New Role */}
              <form onSubmit={handleAssignRole}>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                  {t("admin.userManagement.addRole", "Adaugă rol nou")}
                </h3>
                <div className="space-y-3">
                  <select
                    value={roleFormData.code}
                    onChange={(e) => setRoleFormData({ ...roleFormData, code: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="platform_admin">Admin platformă</option>
                    <option value="club_admin">Admin club</option>
                    <option value="coach">Antrenor</option>
                    <option value="athlete">Sportiv</option>
                  </select>

                  <select
                    value={roleFormData.scope_type}
                    onChange={(e) =>
                      setRoleFormData({
                        ...roleFormData,
                        scope_type: e.target.value as "platform" | "club" | "team",
                      })
                    }
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="platform">Platformă</option>
                    <option value="club">Club</option>
                    <option value="team">Echipă</option>
                  </select>

                  {roleFormData.scope_type === "club" && (
                    <select
                      value={roleFormData.scope_id}
                      onChange={(e) => setRoleFormData({ ...roleFormData, scope_id: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      required
                    >
                      <option value="">{t("admin.userManagement.selectClub", "Selectează club")}</option>
                      {clubs.map((club) => (
                        <option key={club.id} value={club.id}>
                          {club.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRoleModal(false);
                      setSelectedUser(null);
                      resetRoleForm();
                    }}
                    className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-200 font-medium"
                  >
                    {t("common.close", "Închide")}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-medium shadow-lg shadow-blue-500/25"
                  >
                    {t("common.add", "Adaugă")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
